import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useTheme } from 'hooks';
import { SvgWrapper } from 'assets';
import { InstructionsSubDomains } from 'routes';
import { AccessibleIcon } from 'components/partials';
import { usePhotoSeriesStore } from '../../store/root.hook';
import { PhotoSeries } from 'redux/photoseries/photoseries';
import {
  getPhotoSeries,
  getInProgressDamageCapture,
  getDamageCaptureProgress,
} from 'redux/root.selectors';
import { ReactComponent as IconForwardArrow } from 'assets/svgs/forward-arrow.svg';
import { ActionColumn } from '../../containers/action-column/action-column.component';
import { TRANSLATIONS_VALUES_KEYS } from 'redux/internationalization/internationalization';
import { Additional } from 'assets/components/instructions';
import { ImageTypeKeys, ImageTypeNumericKey, IMAGE_TYPES, VehicleTypeKeys } from 'redux/workflows/workflows';
import { ColumnLeft } from './containers';
import { CameraMode } from '../../store/root';
import { useNativeCameraUpload } from '../../hooks/use-native-camera-upload';
import { BikeVin1 } from 'assets/components/instructions/bike';
import { useTranslate } from 'services';
import { useIsDarkTheme } from 'hooks/useTheme/useTheme.util';

export const InstructionWrapper = styled.div`
  display: flex;
  min-height: var(--view-height);
  max-height: var(--view-height);
  overflow: hidden;
  position: relative;
`;

export const InstructionWrapperCol1 = styled.div<{ background: string }>`
  background: ${({ background }) => background};
  backdrop-filter: blur(1.5rem);
  padding: 2.75rem 3.5rem;
  padding-top: 2.875rem;
  flex: 1;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const InstructionWrapperCol2 = styled.div<{ background: string }>`
  background-color: ${({ background }) => background};
  width: var(--toolbar-width);
  margin: 0 auto;
`;

export type Params = {
  imageType: string;
  vehicleType: string;
};

function Instructions() {
  const history = useHistory();
  const { pathname } = useLocation();
  const { imageType, vehicleType } = useParams<Params>();
  const photoSeries: PhotoSeries = useSelector(getPhotoSeries);

  const {
    styles: { colorPrimaryButtonText, colorBackgroundGradient, colorBackgroundBase },
  } = useTheme();
  const {
    instructions,
    captureImage: { addMoreImages },
    dispatchSetInstructionsSelectedLocationIndex
  } = usePhotoSeriesStore();

  const imageSubType = instructions.subType?.subTypes[instructions.subType?.selectedSubTypeIndex].subTypeDigit;
  const nextPath = `/capture/${photoSeries.id}/image-type/${imageType}/vehicle-type/${vehicleType}/image-subType/${imageSubType}`;

  const itsLocationSubDomain = pathname.endsWith(InstructionsSubDomains.location);
  const { currentImagesCaptured, totalImagesToBeCapture } = instructions;
  const shouldAddMoreImages = addMoreImages.itsAddMoreImages || Number(imageType) === ImageTypeKeys.Additional;
  const additionalImagesCaptured = addMoreImages.totalImagesCaptured;

  const inProgressDamageCapture = useSelector(getInProgressDamageCapture);
  const damageCaptureProgress = useSelector(getDamageCaptureProgress);
  const { translate } = useTranslate();

  const { fileInput, mode } = useNativeCameraUpload(nextPath);
  const isDarkTheme = useIsDarkTheme();

  function onClickHandler() {
    const location = instructions.instructions && instructions.instructions.find((l) => l.subTypeDigit === imageSubType);
    const hasNextLocation = itsLocationSubDomain && location!.selectedLocationIndex !== location!.locations.length - 1;

    if (hasNextLocation) {
      dispatchSetInstructionsSelectedLocationIndex(location!.selectedLocationIndex + 1, imageSubType);
    } else if (mode === CameraMode.InBrowser) {
      const captureView = `/capture/${photoSeries.id}/image-type/${imageType}/vehicle-type/${vehicleType}/image-subType/${imageSubType}`;
      history.push(captureView);
    } else if (mode === CameraMode.Native) {
      fileInput?.click();
    }
  }

  const getIcon = React.useMemo(() => {
    let node = <></>;
    const isMotorcycle = Number(vehicleType) === VehicleTypeKeys.Motorcycle;
    const IconFc = IMAGE_TYPES[Number(imageType) as ImageTypeNumericKey].icon;

    if (shouldAddMoreImages) {
      node = (
        <SvgWrapper size="small">
          <Additional />
        </SvgWrapper>
      );
    } else {
      node = <SvgWrapper size="small">{isMotorcycle && Number(imageType) === ImageTypeKeys.Vin ? <BikeVin1 /> : <IconFc />}</SvgWrapper>;
    }
    return node;
  }, [imageType, shouldAddMoreImages, vehicleType]);

  const progressBar: {
    currentStep?: number | null;
    totalSteps?: number | null;
    textNode?: string | React.ReactNode;
    icon: React.ReactSVGElement | React.ReactElement;
    darkMode?: boolean;
  } = {
    icon: getIcon,
    currentStep: currentImagesCaptured,
    totalSteps: totalImagesToBeCapture,
    textNode: undefined,
    darkMode: isDarkTheme
  };

  if (shouldAddMoreImages && inProgressDamageCapture && damageCaptureProgress) {
    progressBar.currentStep = damageCaptureProgress.current + 1;
    progressBar.totalSteps = damageCaptureProgress.total;
  } else if (shouldAddMoreImages) {
    progressBar.currentStep = null;
    progressBar.totalSteps = null;
    progressBar.textNode = <><span>{additionalImagesCaptured}</span> {translate(TRANSLATIONS_VALUES_KEYS.vehicle_scan.images)}</>;
  }

  return (
    <InstructionWrapper className="wrapper">
      <InstructionWrapperCol1
        background={colorBackgroundGradient}
      >
        <ColumnLeft />
      </InstructionWrapperCol1>
      <InstructionWrapperCol2
        background={colorBackgroundBase}
      >
        <ActionColumn
          topRow={{
            progressBar,
            damageLabelling: {
              enabled: false,
              hide: false
            },
          }}
          actionButton={{
            icon: (
              <AccessibleIcon label={translate(TRANSLATIONS_VALUES_KEYS.accessibility.next_btn_text)}>
                <SvgWrapper size="small" fill={colorPrimaryButtonText}>
                  <IconForwardArrow />
                </SvgWrapper>
              </AccessibleIcon>
            ),
            onClickHandler,
            animate: true
          }}
          bottomRow={{}}
        />
      </InstructionWrapperCol2>
    </InstructionWrapper>
  );
}

export default Instructions;
